import { useRef, forwardRef } from 'react';
import {
  type OrderCardData,
  AUTOSHIP_CARD_TYPES,
  AUTOSHIP_ORDER_LIST_COMPONENT_CARD_TYPES,
} from '@chewy/chat-library-common';
import styles from './OrderCardMultiItem.module.scss';
import {
  WidgetContainer,
  StatusDatePanel,
  ProductImage,
  ExpandedQuantityTab,
  ActionButton,
  NavLink,
  Tags,
  AdditionalInfo,
  OrderFrequencyLabel,
  Payments,
  WidgetOverlayButton
} from '@chewy/chat-library-components';
import first from 'lodash/first';
import {
  KibTruncation,
  KibTruncationContent,
  KibTruncationToggle
} from '@chewy/kib-truncation-react';
import '@chewy/kib-truncation-styles/dist/kib-truncation.css';
import { useMaxVisibleItems } from './useMaxVisibleItems';

export interface OrderCardMultiItemProps {
  data: OrderCardData[];
  isSelectable?: boolean;
  isSelected?: boolean;
  isUnselected?: boolean;
  onSelect?: () => void;
}

const OrderCardMultiItem = forwardRef<HTMLDivElement, OrderCardMultiItemProps>(
  (
    { data, isSelectable = false, isSelected, isUnselected, onSelect }: OrderCardMultiItemProps,
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const MAX_VISIBLE_ITEMS = useMaxVisibleItems(containerRef, { initialMax: 2 });
    const orderData = first(data);
    if (!orderData) {
      return null;
    }
    const title = orderData.title ?? '';
    const subscriptionFrequency = orderData.subscriptionFrequency ?? '';
    const items = orderData.items ?? [];
    const remainingCount = items.length - MAX_VISIBLE_ITEMS;
    const dateStatus = orderData.dateStatus ?? { label: '', status: '', date: '' };
    const orderStatus = orderData.orderStatus ?? '';
    const actions = orderData.actions ?? [];

    const hasActions = actions.length > 0;
    const nonLinkActions = actions.filter(action => action.type !== 'link');
    const linkActions = actions.filter(action => action.type === 'link');
    const hasLinkAction = linkActions.length > 0;
    const linkAction = first(linkActions)?.url;
    // Avoid a "+1" badge; show the final thumbnail in the reserved badge slot instead.
    const showBadge = remainingCount > 1;
    const visibleItems = items.slice(0, showBadge ? MAX_VISIBLE_ITEMS : items.length);
    const tags = orderData.tags ?? [];
    const additionalMetaData = orderData.additionalMetaData ?? [];
    const hasAdditionalInfo = additionalMetaData.length > 0;
    const additionalInfoContent = (
      <div className={styles.additionalInfoContainer}>
        <AdditionalInfo additionalInfo={additionalMetaData} />
      </div>
    );
    const usesInlineAdditionalInfo = ['pending vet approval', 'upload authorization'].includes(
      orderStatus.toLowerCase()
    );
    const payments = orderData.payments ?? [];
    const hasPayments = payments.length > 0;
    const isAutoship = AUTOSHIP_CARD_TYPES.includes(orderData.cardType ?? '');
    const hideTagsAndItemName = AUTOSHIP_ORDER_LIST_COMPONENT_CARD_TYPES.includes(orderData.cardType ?? '');
    const shouldShowTags = tags.length > 0 && !hideTagsAndItemName;
    const cardAriaLabel = isSelectable
      ? `${title}${dateStatus.label ? `, ${dateStatus.label}` : ''}`
      : undefined;

    return (
      <WidgetContainer
        isSelectable={isSelectable}
        isSelected={isSelected}
        isUnselected={isUnselected}
        onSelect={onSelect}
        data={orderData}
        hasActions={hasActions}
      >
        <div ref={ref} className={styles.orderContainer}>
          <div
            ref={containerRef}
            className={`${isAutoship ? styles.autoshipInfo : styles.orderInfo}`}
          >
            <div
              className={`${styles.orderInfoContent} ${isAutoship ? styles.orderInfoContentAutoship : ''}`}
            >
              <h3 className={styles.orderNumber}>
                {isSelectable ? (
                  <WidgetOverlayButton text={title} ariaLabel={cardAriaLabel} />
                ) : (
                  title
                )}
              </h3>
              <OrderFrequencyLabel frequency={subscriptionFrequency} />
              <StatusDatePanel
                isAutoship={isAutoship}
                orderStatus={orderStatus}
                dateStatus={dateStatus}
              />
              {usesInlineAdditionalInfo && hasAdditionalInfo && additionalInfoContent}
            </div>
            <KibTruncation>
              <KibTruncationContent
                summary={
                  <>
                    <div className={`${styles.itemsGrid}`}>
                      {visibleItems.map((item, index) => (
                        <ProductImage
                          key={index}
                          imageUrl={item.imageUrl || ''}
                          name={item.name || 'Order item'}
                          quantity={item.quantity}
                        />
                      ))}
                      {showBadge && <ExpandedQuantityTab remainingCount={remainingCount} />}
                    </div>
                  </>
                }
              >
                <div className={`${styles.itemsGrid} ${styles.expanded}`}>
                  {items.map((item, index) => (
                    <ProductImage
                      key={index}
                      imageUrl={item.imageUrl || ''}
                      name={item.name || 'Order item'}
                      quantity={item.quantity}
                    />
                  ))}
                </div>
              </KibTruncationContent>
              {showBadge && !hasActions && (
                <span data-kib-truncation-toggle>
                  <KibTruncationToggle
                    className={styles.truncationToggle}
                    labelMore="Show all"
                    labelLess="Show less"
                  />
                </span>
              )}
            </KibTruncation>
          </div>
          {shouldShowTags && (
            <div className={styles.tagsContainer}>
              <Tags tags={tags} />
            </div>
          )}
          {!usesInlineAdditionalInfo && hasAdditionalInfo && additionalInfoContent}
          {hasPayments && (
            <div className={styles.paymentsContainer}>
              <Payments payments={payments} />
            </div>
          )}
          {(nonLinkActions.length > 0 || hasLinkAction) && (
            <div className={styles.actionsContainer}>
              {nonLinkActions.map((action, index) => (
                <ActionButton
                  key={index}
                  action={action}
                  widgetData={orderData}
                  className={styles.actionButton}
                />
              ))}
              {hasLinkAction && (
                <div className={styles.linkActionContainer}>
                  <NavLink
                    href={linkAction || ''}
                    label={first(linkActions)?.text || ''}
                    action={first(linkActions)!}
                    widgetData={orderData}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </WidgetContainer>
    );
  }
);

export default OrderCardMultiItem;
